import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import MainNavigationInfo from "components/mainNavInfo";
import {
	getDuration,
	getFormattedDate,
	getFullName,
	hasPermissions,
	useGetUserUrl,
	useIsFeatureEnabled,
	userPermissionSet,
} from "helpers";
import { UnderwritingTabs, UWDecisions } from "types";

import AssignItem from "./assignItem";
import AssignItemConcurrence from "./assignItemConcurrence";
import AssignItemQa from "./assignItemQa";
import JarisItem from "./jarisItem";

import styles from "./underwritingNavInfo.module.scss";

const UnderwritingNavigationInfo = ({
	refetchApplicationInfo,
	applicationInfo,
}) => {
	const { tab } = useParams<{ tab: string }>();

	const merchantInfo = useSelector(
		(state: any) => state.merchantInfo.merchantInfo,
	);
	const isMerchantInfoLoading = useSelector(
		(state: any) => state.merchantInfo.loading,
	);
	const isHistorical = tab === UnderwritingTabs.Historical.name;
	const isQAComplete = tab === UnderwritingTabs.QAComplete.name;

	const decision = UWDecisions.getByName(
		merchantInfo?.underwritingDecision,
	)?.displayName;

	const assigneeConcurrence = useMemo(
		() => merchantInfo?.concurrence_assignee,
		[merchantInfo?.concurrence_assignee],
	);

	// UW tabs
	const assignee = useMemo(
		() => merchantInfo?.assignee,
		[merchantInfo?.assignee],
	);

	const startDate = merchantInfo?.underwriting_started_at;
	const completedDate = merchantInfo?.underwriting_completed_at;
	const startedDateTime = getFormattedDate({
		date: startDate,
	});
	const completedDateTime = getFormattedDate({
		date: completedDate,
	});

	const duration = isHistorical
		? getDuration({ start: startDate, end: completedDate })
		: getDuration({ start: startDate });

	const completedBy = merchantInfo?.completed_by;
	const completedByUrl = useGetUserUrl(completedBy);

	// UW QA tabs
	const assigneeQA = useMemo(
		() => merchantInfo?.underwriting_qa_assignee,
		[merchantInfo?.underwriting_qa_assignee],
	);

	const startDateQA = merchantInfo?.underwriting_qa_started_at;
	const completedDateQA = merchantInfo?.underwriting_qa_completed_at;
	const startedDateTimeQA = getFormattedDate({
		date: startDateQA,
	});
	const completedDateTimeQA = getFormattedDate({
		date: completedDateQA,
	});

	const durationQA = isQAComplete
		? getDuration({ start: startDateQA, end: completedDateQA })
		: getDuration({ start: startDateQA });

	const completedByQA = merchantInfo?.underwriting_qa_completed_by;
	const completedByUrlQA = useGetUserUrl(completedByQA);

	const jarisEnabled = useIsFeatureEnabled("MMS-196-Jaris-POC");

	const jarisItems = useMemo(
		() =>
			applicationInfo?.jarisApplicationId && jarisEnabled
				? [
						{
							label: "Jaris Status",
							data: (
								<JarisItem
									jarisApplicationId={
										applicationInfo?.jarisApplicationId
									}
									status={
										applicationInfo?.jarisApplicationState
									}
								/>
							),
						},
				  ]
				: [],
		[
			applicationInfo?.jarisApplicationId,
			applicationInfo?.jarisApplicationState,
			jarisEnabled,
		],
	);

	const underwritingItems = useMemo(
		() => [
			{
				label: "Age in UW",
				data: startedDateTime ? `${startedDateTime} ${duration}` : "-",
			},
			{
				label: "Assigned to",
				data: (
					<AssignItem
						assignee={assignee}
						applicationId={merchantInfo?.applicationId}
						isLoading={isMerchantInfoLoading}
						refetchApplicationInfo={refetchApplicationInfo}
					/>
				),
			},
			...jarisItems,
		],
		[
			startedDateTime,
			duration,
			assignee,
			merchantInfo?.applicationId,
			isMerchantInfoLoading,
			refetchApplicationInfo,
			jarisItems,
		],
	);

	const historicalItems = useMemo(
		() => [
			{
				label: "Underwriting Decision",
				data: decision || "-",
			},
			{
				label: "Age in UW",
				data: startedDateTime ? `${startedDateTime} ${duration}` : "-",
			},
			{
				label: "UW Complete on",
				data: completedDateTime || "-",
			},
			{
				label: "UW Complete by",
				data: getFullName(completedBy),
				path: completedByUrl,
				guard: () => completedBy && hasPermissions(userPermissionSet),
			},
			{
				label: "Assigned to",
				data: (
					<AssignItemConcurrence
						assignee={assigneeConcurrence}
						applicationId={merchantInfo?.applicationId}
						isLoading={isMerchantInfoLoading}
						refetchApplicationInfo={refetchApplicationInfo}
					/>
				),
				hidden: isHistorical,
			},
			...jarisItems,
		],
		[
			assigneeConcurrence,
			completedBy,
			completedByUrl,
			completedDateTime,
			decision,
			duration,
			isHistorical,
			isMerchantInfoLoading,
			merchantInfo?.applicationId,
			refetchApplicationInfo,
			startedDateTime,
			jarisItems,
		],
	);

	const underwritingQAItems = useMemo(
		() => [
			{
				label: "Age in UW QA",
				data: startedDateTimeQA
					? `${startedDateTimeQA} ${durationQA}`
					: "-",
			},
			{
				label: "Assigned to",
				data: (
					<AssignItemQa
						assignee={assigneeQA}
						applicationId={merchantInfo?.applicationId}
						isLoading={isMerchantInfoLoading}
						refetchApplicationInfo={refetchApplicationInfo}
					/>
				),
			},
			...jarisItems,
		],
		[
			assigneeQA,
			durationQA,
			isMerchantInfoLoading,
			merchantInfo?.applicationId,
			refetchApplicationInfo,
			startedDateTimeQA,
			jarisItems,
		],
	);

	const qaCompleteItems = useMemo(
		() => [
			{
				label: "Underwriting Decision",
				data: decision || "-",
			},
			{
				label: "Age in UW QA",
				data: startedDateTimeQA
					? `${startedDateTimeQA} ${durationQA}`
					: "-",
			},
			{
				label: "UW Complete on",
				data: completedDateTimeQA || "-",
			},
			{
				label: "UW Complete by",
				data: getFullName(completedByQA),
				path: completedByUrlQA,
				guard: () => completedByQA && hasPermissions(userPermissionSet),
			},
			...jarisItems,
		],
		[
			completedByQA,
			completedByUrlQA,
			completedDateTimeQA,
			durationQA,
			startedDateTimeQA,
			decision,
			jarisItems,
		],
	);

	const navInfoItems = tab => {
		switch (tab) {
			case UnderwritingTabs.Historical.name:
				return historicalItems;
			case UnderwritingTabs.Concurrence.name:
				return historicalItems;
			case UnderwritingTabs.QAUnassigned.name:
				return underwritingQAItems;
			case UnderwritingTabs.QAInProgress.name:
				return underwritingQAItems;
			case UnderwritingTabs.QAComplete.name:
				return qaCompleteItems;
			default:
				return underwritingItems;
		}
	};

	return (
		<MainNavigationInfo
			content={navInfoItems(tab)}
			className={styles.underwritingNavInfo}
			loading={isMerchantInfoLoading}
		/>
	);
};

export default UnderwritingNavigationInfo;
